import './rater.coverage.list.detail.scss';

import {
  Box,
  FormLabel,
  TableCell,
  TableRow,
  Typography,
  TypographyProps,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ConvergeTooltip,
  FormToggleSwitchField,
} from '../../../../../presentation';
import { SubCoverageUuid } from '@converge/data';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import {
  FusionScoreModifierDisplay,
  RaterNotesButton,
  RaterNotesRow,
} from '../../common';
import {
  IUnderwritingCoverage,
  IUnderwritingSubCoverage,
  NoteTargetName,
  NoteTargetType,
} from '@converge/graphql';
import {
  RaterCoverageListDetailContext,
  useRaterCoverageListDetailContext,
} from './rater.coverage.list.detail.context';
import {
  displayFormatDate,
  displayNumber,
  formatDollarAmount,
  limitNumberToTwoDigits,
} from '@converge/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../../data';

import { CascadeFusionScoreModifierButton } from '../cascade-fusion-score-modifier.button';
import { CascadeLimitAndRetentionButton } from '../cascade-limit-and-retention.button';
import { CoverageUuid, numberFieldMaxValue } from '@converge/data';
import { NumberField } from '../../common/number.field';
import { RaterCoverageListDetailExpandedCells } from './rater.coverage.list.expanded.cells';
import { RaterRetroactiveDate } from '../rater.retroactive-date';
import { Stack } from '@mui/system';
import config from '@converge/config';
import { coverageHasValidationError } from './utils';
import { useRaterCoverageUpdates } from './rater.coverage.list.detail.updates';
import { useUnderwritingSubmissionStateContext } from '../../../underwriting.submission.context';

export interface IRaterCoverageListDetailProps<
  Coverage extends
    | IUnderwritingSubCoverage
    | IUnderwritingCoverage = IUnderwritingCoverage
> {
  coverage: Coverage;
  parentCoverage?: IUnderwritingCoverage;
  index?: number;
  isLastRow: boolean;
  isEditable: boolean;
  parentCoverageLimitError?: number | null;
  parentCoverageRetentionError?: number | null;
}

export const RaterCoverageListDetail = (
  props: IRaterCoverageListDetailProps
) => {
  const {
    coverage,
    parentCoverage,
    index,
    isLastRow,
    parentCoverageLimitError,
    parentCoverageRetentionError,
  } = props;
  const { dispatch, actions } = useRaterActionDispatchContext();
  const {
    isRequestingRatingUpdate,
    showExpandedView,
    columnCount,
    isEditable,
    raterError,
    raterErrorDetails = [],
    upsertQuoteIsLoading,
    isRequestingUpsertQuote,
    quote: { inForcePolicy },
  } = useRaterStateContextSelector(
    ({
      isRequestingRatingUpdate,
      showExpandedView,
      columnCount,
      isEditable,
      raterError,
      raterErrorDetails,
      upsertQuoteIsLoading,
      isRequestingUpsertQuoteNow,
      isRequestingUpsertQuoteAfterRating,
      quote,
    }) => {
      return {
        isRequestingRatingUpdate,
        showExpandedView,
        columnCount,
        isEditable,
        raterError,
        raterErrorDetails,
        upsertQuoteIsLoading,
        isRequestingUpsertQuote:
          isRequestingUpsertQuoteAfterRating || isRequestingUpsertQuoteNow,
        quote,
      };
    }
  );

  const {
    name,
    uuid,
    features,
    hasChanged,
    quote: {
      included,
      basePremium,
      modifiedCoveragePremium,
      minFusionScoreModifier,
      maxFusionScoreModifier,
      technicalPremium,
      fusionScore,
      hazardGroup,
      uuid: quoteUuid,
      limitRetentionFactor,
      priorActsFactor,
      waitingPeriodFactor,
      restorationPeriodFactor,
      policyTermFactor,
      sharedPolicyAggregateFactor,
      commissionAdjustmentFactor,
      sharedCoverageAggregateFactor,
      subCoverageFactor,
      enhancedMediaFactor,
      fullPriorActs,
      ratedFusionScoreModifier,
      selectedFusionScoreModifier,
    } = {},
    subCoverages = [],
  } = coverage;

  const updateCoverage = useCallback(
    ({ update, ...coverage }: IUnderwritingCoverage & { update: boolean }) => {
      dispatch(
        actions.updateCoverage(
          parentCoverage
            ? {
                ...parentCoverage,
                subCoverages: parentCoverage.subCoverages.map((subCoverage) => {
                  if (subCoverage.uuid === coverage.uuid) {
                    return {
                      ...subCoverage,
                      ...(coverage as unknown as IUnderwritingSubCoverage),
                      hasChanged: true,
                    };
                  }
                  return subCoverage;
                }),
                update,
              }
            : {
                ...coverage,
                subCoverages: coverage.subCoverages.map((subCoverage) => {
                  const shouldInclude =
                    included !== coverage?.quote?.included
                      ? coverage?.quote?.included
                      : !!subCoverage?.quote?.included;
                  return {
                    ...subCoverage,
                    hasChanged: shouldInclude !== subCoverage.quote?.included,
                    quote: {
                      ...subCoverage?.quote,
                      included: shouldInclude,
                    },
                  };
                }),
                update,
              }
        )
      );
    },
    [actions, dispatch, included, parentCoverage]
  );

  const { retentionField, limitField } = {
    retentionField: parentCoverage
      ? ('subRetention' as const)
      : ('retention' as const),
    limitField: parentCoverage ? ('subLimit' as const) : ('limit' as const),
  };
  const {
    info: { submission },
  } = useUnderwritingSubmissionStateContext();

  const submissionStartDate =
    submission && 'policyStartDate' in submission
      ? submission?.policyStartDate
      : '';

  const retroactiveDate =
    coverage?.quote?.retroactiveDate ?? submissionStartDate;
  const { retentionValue, limitValue } = {
    retentionValue: coverage.quote?.[retentionField as unknown as 'retention'],
    limitValue: coverage.quote?.[limitField as unknown as 'limit'],
  };

  const [limitErrorValue, setLimitErrorValue] = useState<number | null>(null);

  const nameForError = parentCoverage
    ? `${parentCoverage.name} / ${name}`
    : name;

  const {
    handleUpdateLimit,
    handleUpdateRetention,
    handleUpdateFusionScoreModifier,
    retentionErrorValue,
    currentLimit,
    currentRetention,
    coverageLimitErrorValue,
    parentCoverageRetentionValue,
  } = useRaterCoverageUpdates({
    parentCoverage,
    updateCoverage,
    nameForError,
    limitField,
    retentionField,
    limitValue,
    coverage,
    retentionValue,
    setLimitErrorValue,
  });

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasErrors = (fieldName: string) => {
    return errors &&
      errors[fieldName] &&
      nameForError &&
      nameForError in (errors[fieldName] as FieldErrors<FieldValues>)
      ? true
      : false;
  };
  const isFirstRow = index === 0;

  const hasInvalidSelectedFusionScoreModifier = hasErrors(
    'selectedFusionScoreModifier'
  );
  const hasInvalidSubLimit = hasErrors('subLimit');
  const hasInvalidSubRetention = hasErrors('subRetention');

  const hasInvalidData =
    hasInvalidSelectedFusionScoreModifier ||
    hasInvalidSubLimit ||
    hasInvalidSubRetention;

  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidData));
  }, [actions, dispatch, hasInvalidData, raterError]);

  const [retentionLive, setRetentionLive] = useState(retentionValue ?? null);
  const [limitLive, setLimitLive] = useState(limitValue ?? null);

  const {
    submission: { coverageLevelScoring },
  } = useRaterStateContextSelector(({ submission }) => {
    return {
      submission,
    };
  });

  const parentCoverageLimit =
    parentCoverageLimitError ?? parentCoverage?.quote?.limit ?? 0;
  const subLimitValue =
    currentLimit ?? coverage?.quote?.subLimit ?? limitErrorValue ?? 0;

  const parentCoverageRetention =
    parentCoverageRetentionError ?? parentCoverage?.quote?.retention ?? 0;
  const subRetentionValue =
    retentionErrorValue ??
    currentRetention ??
    coverage?.quote?.subRetention ??
    0;

  useEffect(() => {
    if (!raterError || Object.keys(raterError).length === 0) {
      clearErrors();
    }
  }, [clearErrors, raterError]);

  const validateSubCoverage = useCallback(
    (field: string, errorType: string, message: string) => {
      if (raterError && raterError.length > 0) {
        setError(field, {
          type: errorType,
          message,
        });
      }
    },
    [raterError, setError]
  );

  useEffect(() => {
    if (parentCoverage?.quote && parentCoverageRetention < subRetentionValue) {
      validateSubCoverage(
        `subRetention.${nameForError}`,
        'SubRetention',
        `Sub-retention must be less than or equal to its parent coverage retention of ${formatDollarAmount(
          parentCoverage?.quote?.retention
        )}`
      );
    }
  }, [
    validateSubCoverage,
    nameForError,
    parentCoverage,
    parentCoverageRetention,
    subRetentionValue,
  ]);

  useEffect(() => {
    if (parentCoverage?.quote && parentCoverageLimit < subLimitValue) {
      validateSubCoverage(
        `subLimit.${nameForError}`,
        'SubLimit',
        `Sub-Limit must be less than or equal to its parent coverage limit of ${formatDollarAmount(
          parentCoverage?.quote?.subLimit
        )}`
      );
    }
  }, [
    validateSubCoverage,
    nameForError,
    parentCoverage,
    subLimitValue,
    parentCoverageLimit,
  ]);

  const coverageErrorLimit = coverageHasValidationError({
    raterErrorDetails,
    index,
    type: 'limit',
  });
  const coverageErrorRetention = coverageHasValidationError({
    raterErrorDetails,
    index,
    type: 'retention',
  });

  const renderExpandedView = () => (
    <RaterCoverageListDetailExpandedCells
      included={included}
      isSubCoverage={!!parentCoverage}
      limitRetentionFactor={limitRetentionFactor}
      priorActsFactor={priorActsFactor}
      waitingPeriodFactor={waitingPeriodFactor}
      restorationPeriodFactor={restorationPeriodFactor}
      policyTermFactor={policyTermFactor}
      sharedPolicyAggregateFactor={sharedPolicyAggregateFactor}
      commissionAdjustmentFactor={commissionAdjustmentFactor}
      sharedCoverageAggregateFactor={sharedCoverageAggregateFactor}
      subCoverageFactor={subCoverageFactor}
      enhancedMediaFactor={enhancedMediaFactor}
      isRequestingRatingUpdate={isRequestingRatingUpdate}
    />
  );

  const [selectedRetroActiveDate, setSelectedRetroActiveDate] =
    useState<Date | null>(retroactiveDate);
  const isRetroactiveDateOrFullPriorActs = useMemo(() => {
    return (
      quoteUuid &&
      [
        CoverageUuid.NetworkSecurityPrivacyLiability,
        CoverageUuid.RegulatoryProceedings,
        CoverageUuid.PciDssEvents,
      ].includes(coverage?.uuid as CoverageUuid)
    );
  }, [coverage, quoteUuid]);

  const isReadOnly =
    !included ||
    !isEditable ||
    upsertQuoteIsLoading ||
    isRequestingUpsertQuote ||
    inForcePolicy ||
    false;

  return (
    <>
      <RaterCoverageListDetailContext.Provider
        value={{
          ...props,
          updateCoverage,
          isEditable:
            isEditable && !upsertQuoteIsLoading && !isRequestingUpsertQuote,
        }}
      >
        <TableRow
          data-uuid={uuid}
          data-type={parentCoverage ? 'subcoverage' : 'coverage'}
          sx={{
            background: hasInvalidData
              ? `rgba(219, 82, 76, ${parentCoverage ? 0.15 : 0.1})`
              : hasChanged
              ? 'rgb(253, 245, 231, 0.75)'
              : parentCoverage
              ? 'rgba(0, 0, 0, 0.02)'
              : undefined,
            '& .cascade-button': {
              opacity: 0,
              transition: '0.2s opacity',
            },
            '&:hover .cascade-button': {
              opacity: 1,
            },
          }}
        >
          <TableCell
            sx={{
              paddingLeft: parentCoverage ? 5 : undefined,
              position: 'relative',
            }}
          >
            <ConvergeTooltip
              title={
                !hasChanged
                  ? `Last changed ${displayFormatDate(
                      coverage.updatedAt,
                      displayFormatDate.DateFormatType
                        .USADateFormatWithTimeAndSecondsAt
                    )}`
                  : 'Has changed'
              }
            >
              <Typography
                fontWeight={parentCoverage ? undefined : 'bold'}
                fontSize="14px"
                display={'inline'}
              >
                {name}
              </Typography>
            </ConvergeTooltip>
          </TableCell>
          <TableCell className="hazard-cell">
            {!parentCoverage && (
              <RaterCoverageListDetail.Value
                isDisabled={!included || inForcePolicy}
                color={
                  hazardGroup && hazardGroup >= config.riskyHazardGroup
                    ? 'error'
                    : undefined
                }
                isBold={
                  hazardGroup ? hazardGroup >= config.riskyHazardGroup : false
                }
              >
                {hazardGroup}
              </RaterCoverageListDetail.Value>
            )}
          </TableCell>
          <TableCell>
            <Stack className="toggle-button" direction="row">
              <FormToggleSwitchField.Switch
                disabled={
                  (parentCoverage && !parentCoverage.quote?.included) ||
                  !isEditable ||
                  upsertQuoteIsLoading ||
                  isRequestingUpsertQuote ||
                  inForcePolicy
                }
                onChange={(event, included) => {
                  updateCoverage({
                    ...coverage,
                    quote: {
                      ...coverage.quote,
                      included,
                    },
                    update: true,
                  });
                }}
                checked={included ?? false}
              />
              {isRetroactiveDateOrFullPriorActs && (
                <Box className="isRetroactive-date-FullPriorActs">
                  <RaterRetroactiveDate
                    uuid={uuid}
                    isDisabled={isReadOnly}
                    retroactiveDate={selectedRetroActiveDate}
                    fullPriorActs={fullPriorActs}
                    onRetroactiveDateChange={(newRetroactiveDate) => {
                      const date = newRetroactiveDate?.toDate() || null;
                      setSelectedRetroActiveDate(date);
                      dispatch(
                        actions.setCoveragesRetroactiveDate(uuid, date, false)
                      );
                    }}
                    onFullPriorActsChange={(fullPriorActs) => {
                      dispatch(
                        actions.setCoveragesRetroactiveDate(
                          uuid,
                          retroactiveDate,
                          fullPriorActs,
                          true
                        )
                      );
                    }}
                  />
                </Box>
              )}
            </Stack>
          </TableCell>
          <TableCell sx={{ padding: 0, margin: 0 }}>
            {/* Show info icon only for Business Income Coverage with Proof of Loss */}
            {uuid === SubCoverageUuid.ProofOfLoss ? (
              <Box display="flex" justifyContent="end" alignItems="end">
                <ConvergeTooltip title="Changing Proof of Loss limits or retentions will apply to all amounts under Business Income Coverage.">
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: '16px',
                      cursor: 'default',
                    }}
                  />
                </ConvergeTooltip>
              </Box>
            ) : null}
          </TableCell>
          <TableCell>
            <Box minWidth={130}>
              <NumberField
                isCurrency
                name={`${name}:limit`}
                value={limitValue ?? null}
                max={numberFieldMaxValue}
                min={0}
                error={hasInvalidSubLimit || coverageErrorLimit}
                isDisabled={isReadOnly}
                onChange={(val) => {
                  setLimitLive(val);
                }}
                onBlur={handleUpdateLimit}
              />
            </Box>
          </TableCell>
          <TableCell>
            <Box minWidth={100}>
              <NumberField
                isCurrency
                name={`${name}:retention`}
                value={retentionValue ?? null}
                placeholder={!features.enableRetention ? 'N/A' : undefined}
                max={numberFieldMaxValue}
                error={hasInvalidSubRetention || coverageErrorRetention}
                isDisabled={isReadOnly}
                onChange={(val) => {
                  setRetentionLive(val);
                }}
                onBlur={handleUpdateRetention}
              />
            </Box>
          </TableCell>

          <TableCell>
            {!parentCoverage &&
              isEditable &&
              !upsertQuoteIsLoading &&
              !isRequestingUpsertQuote &&
              !inForcePolicy &&
              limitLive !== 0 &&
              retentionLive !== 0 && (
                <Stack className="cascade-button cascade-container">
                  <CascadeLimitAndRetentionButton
                    limit={limitValue}
                    retention={retentionValue}
                    isFirstRow={isFirstRow}
                    isLastRow={isLastRow}
                    isCyberCrime={coverage.name === 'Cyber Crime'}
                  />
                </Stack>
              )}
          </TableCell>

          {showExpandedView && renderExpandedView()}

          <TableCell aria-label="Base premium">
            <RaterCoverageListDetail.Value
              isDisabled={
                upsertQuoteIsLoading ||
                isRequestingUpsertQuote ||
                !included ||
                isRequestingRatingUpdate
              }
              isBold={included ?? false}
            >
              {formatDollarAmount(basePremium)}
            </RaterCoverageListDetail.Value>
          </TableCell>
          <TableCell aria-label="Technical premium">
            <RaterCoverageListDetail.Value
              isDisabled={
                upsertQuoteIsLoading ||
                isRequestingUpsertQuote ||
                isRequestingRatingUpdate ||
                !included
              }
              isBold={included ?? false}
            >
              {formatDollarAmount(technicalPremium)}
            </RaterCoverageListDetail.Value>
          </TableCell>
          <TableCell>
            {!parentCoverage && (
              <RaterCoverageListDetail.Value
                isDisabled={
                  upsertQuoteIsLoading ||
                  isRequestingUpsertQuote ||
                  isRequestingRatingUpdate ||
                  !included
                }
              >
                {fusionScore && displayNumber(fusionScore)}
              </RaterCoverageListDetail.Value>
            )}
          </TableCell>
          <TableCell>
            {!parentCoverage && (
              <Stack className="fusionScore-modifier-container" spacing={1}>
                <Box className="fusionScore-modifier">
                  <NumberField
                    name={`selectedFusionScoreModifier.${name}`}
                    placeholder={ratedFusionScoreModifier?.toFixed(2)}
                    value={
                      selectedFusionScoreModifier
                        ? limitNumberToTwoDigits(selectedFusionScoreModifier)
                        : null
                    }
                    max={maxFusionScoreModifier ?? 100}
                    min={minFusionScoreModifier ?? 100}
                    isDisabled={isRequestingRatingUpdate || isReadOnly}
                    onBlur={handleUpdateFusionScoreModifier}
                    error={hasInvalidSelectedFusionScoreModifier}
                    asValue
                  />
                </Box>
                <FusionScoreModifierDisplay label="Min">
                  {minFusionScoreModifier ?? 'N/A'}
                </FusionScoreModifierDisplay>
                <FusionScoreModifierDisplay label="Max">
                  {maxFusionScoreModifier ?? 'N/A'}
                </FusionScoreModifierDisplay>
                <TableCell className="cascade-button-container">
                  {!parentCoverage &&
                    isEditable &&
                    !upsertQuoteIsLoading &&
                    !isRequestingUpsertQuote &&
                    !inForcePolicy && (
                      <Stack className="cascade-button cascade-container">
                        {coverageLevelScoring === false && (
                          <CascadeFusionScoreModifierButton
                            fusionScoreModifier={
                              selectedFusionScoreModifier
                                ? limitNumberToTwoDigits(
                                    selectedFusionScoreModifier
                                  )
                                : null
                            }
                            isFirstRow={isFirstRow}
                            isLastRow={isLastRow}
                          />
                        )}
                      </Stack>
                    )}
                </TableCell>
              </Stack>
            )}
          </TableCell>
          <TableCell aria-label="Modified premium">
            <RaterCoverageListDetail.Value
              isDisabled={
                upsertQuoteIsLoading ||
                isRequestingUpsertQuote ||
                isRequestingRatingUpdate ||
                !included
              }
              isBold={included ?? false}
            >
              {formatDollarAmount(modifiedCoveragePremium)}
            </RaterCoverageListDetail.Value>
          </TableCell>
          <TableCell aria-label="Notes">
            <RaterNotesButton
              targetName={
                parentCoverage
                  ? NoteTargetName.HasSubCoverageType
                  : NoteTargetName.HasCoverageType
              }
              targetType={NoteTargetType.Relationship}
              uuid={quoteUuid ?? ''}
            />
          </TableCell>
        </TableRow>
        {(features.enableWaitingPeriodHours ||
          features.enableWaitingPeriodDays ||
          features.enableRestorationPeriodDays) && (
          <TableRow className="waiting-period-days-container">
            <TableCell colSpan={columnCount}>
              <Stack
                className="waiting-period-and-restoration-day-table"
                paddingLeft={parentCoverage ? 5 : undefined}
              >
                {features.enableWaitingPeriodHours && (
                  <WaitingPeriodHoursField />
                )}
                {features.enableWaitingPeriodDays && (
                  <WaitingPeriodDaysField isReadOnly={inForcePolicy} />
                )}
                {features.enableRestorationPeriodDays && (
                  <RestorationPeriodDaysField isReadOnly={inForcePolicy} />
                )}
              </Stack>
            </TableCell>
          </TableRow>
        )}
        <RaterNotesRow
          uuid={quoteUuid ?? ''}
          targetName={
            parentCoverage
              ? NoteTargetName.HasSubCoverageType
              : NoteTargetName.HasCoverageType
          }
          targetType={NoteTargetType.Relationship}
          colSpan={columnCount}
        />
      </RaterCoverageListDetailContext.Provider>
      {subCoverages.map((subCoverage) => {
        return (
          <RaterCoverageListDetail
            key={subCoverage.uuid}
            coverage={subCoverage as unknown as IUnderwritingCoverage}
            parentCoverage={coverage}
            // handleParentCoverageLimitError={handleParentCoverageLimitError}
            parentCoverageLimitError={coverageLimitErrorValue}
            parentCoverageRetentionError={parentCoverageRetentionValue}
            isLastRow={false}
            isEditable={
              !upsertQuoteIsLoading &&
              !isRequestingUpsertQuote &&
              !isRequestingRatingUpdate &&
              isEditable
            }
          />
        );
      })}
    </>
  );
};

const RestorationPeriodDaysField = ({
  isReadOnly,
}: {
  isReadOnly?: boolean;
}) => {
  const {
    coverage,
    coverage: {
      slug,
      quote: { restorationPeriodDays, included },
    },
    updateCoverage,
    isEditable,
  } = useRaterCoverageListDetailContext();

  return (
    <>
      <Box padding={1} marginLeft={2}>
        <FormLabel>Restoration Period Days</FormLabel>
      </Box>
      <Box className="restoration-or-waiting-field">
        <NumberField
          name={`${slug}:restorationPeriodDays`}
          value={restorationPeriodDays ?? null}
          max={numberFieldMaxValue}
          isDisabled={!included || !isEditable || isReadOnly}
          onBlur={(restorationPeriodDays) => {
            updateCoverage({
              ...coverage,
              quote: {
                ...coverage.quote,
                restorationPeriodDays,
              },
              update: true,
            });
          }}
        />
      </Box>
    </>
  );
};

const WaitingPeriodDaysField = ({ isReadOnly }: { isReadOnly?: boolean }) => {
  const {
    coverage,
    coverage: {
      slug,
      quote: { waitingPeriodDays, included },
    },
    updateCoverage,
    isEditable,
  } = useRaterCoverageListDetailContext();

  return (
    <>
      <Box padding={1}>
        <FormLabel>Waiting period days</FormLabel>
      </Box>
      <Box className="restoration-or-waiting-field">
        <NumberField
          name={`${slug}:waitingPeriodDays`}
          value={waitingPeriodDays ?? null}
          max={numberFieldMaxValue}
          isDisabled={!included || !isEditable || isReadOnly}
          onBlur={(waitingPeriodDays) => {
            updateCoverage({
              ...coverage,
              quote: {
                ...coverage.quote,
                waitingPeriodDays: waitingPeriodDays ?? undefined,
              },
              update: true,
            });
          }}
        />
      </Box>
    </>
  );
};

const WaitingPeriodHoursField = () => {
  const {
    coverage,
    coverage: {
      slug,
      quote: { waitingPeriodHours, included },
    },
    updateCoverage,
    isEditable,
  } = useRaterCoverageListDetailContext();

  const {
    quote: { inForcePolicy },
  } = useRaterStateContextSelector(({ quote }) => {
    return {
      quote,
    };
  });
  return (
    <>
      <Box padding={1}>
        <FormLabel>Waiting Period Hours</FormLabel>
      </Box>
      <Box className="restoration-or-waiting-field">
        <NumberField
          name={`${slug}:waitingPeriodHours`}
          value={waitingPeriodHours ?? null}
          max={numberFieldMaxValue}
          isDisabled={!included || !isEditable || inForcePolicy}
          onBlur={(waitingPeriodHours) => {
            updateCoverage({
              ...coverage,
              quote: {
                ...coverage.quote,
                waitingPeriodHours,
              },
              update: true,
            });
          }}
        />
      </Box>
    </>
  );
};

interface IRaterCoverageListDetailValueProps extends TypographyProps {
  isDisabled?: boolean;
  isBold?: boolean;
}

RaterCoverageListDetail.Value = ({
  children,
  isDisabled,
  isBold,
  ...props
}: IRaterCoverageListDetailValueProps) => {
  return (
    <Typography
      component={'span'}
      fontWeight={isBold ? 'bold' : undefined}
      sx={{
        opacity: isDisabled ? 0.5 : undefined,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
